import { useEffect, useState } from 'react';
import { getRetryQueue, retryQueueAction, GetRetryQueueOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { RefreshCw, RotateCcw, X, Loader2 } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

type QueueItem = GetRetryQueueOutputType['items'][0];

const statusColor: Record<string, string> = {
  Pending: 'bg-accent text-accent-foreground',
  Retrying: 'bg-primary/15 text-primary',
  Completed: 'bg-emerald-500/15 text-emerald-400',
  Failed: 'bg-destructive/15 text-destructive',
};

export default function RetryQueueTab() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [counts, setCounts] = useState({ pending: 0, failed: 0, completed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [acting, setActing] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    getRetryQueue({ status: statusFilter, operationType: typeFilter })
      .then(d => { setItems(d.items); setCounts(d.counts); })
      .catch(() => toast.error('Failed to load retry queue'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter, typeFilter]);

  const handleAction = async (id: string, action: 'retry' | 'dismiss') => {
    setActing(id);
    try {
      await retryQueueAction({ id, action });
      toast.success(action === 'retry' ? 'Re-queued for retry' : 'Dismissed');
      load();
    } catch { toast.error('Action failed'); }
    finally { setActing(null); }
  };

  const timeAgo = (d?: string) => {
    if (!d) return '—';
    try { return formatDistanceToNow(parseISO(d), { addSuffix: true }); } catch { return '—'; }
  };

  const timeUntil = (d?: string) => {
    if (!d) return '—';
    try {
      const date = parseISO(d);
      if (date <= new Date()) return 'Due now';
      return formatDistanceToNow(date, { addSuffix: false }) + ' left';
    } catch { return '—'; }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">Retry Queue</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Failed outbound operations — auto-retried with exponential backoff</p>
        </div>
        <div className="flex items-center gap-2">
          {counts.pending > 0 && (
            <span className="px-2 py-1 rounded-md bg-accent text-xs font-medium text-accent-foreground">
              {counts.pending} pending
            </span>
          )}
          {counts.failed > 0 && (
            <span className="px-2 py-1 rounded-md bg-destructive/15 text-xs font-medium text-destructive">
              {counts.failed} failed
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Retrying">Retrying</SelectItem>
            <SelectItem value="Failed">Failed</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="BMS Bridge">BMS Bridge</SelectItem>
            <SelectItem value="SMS">SMS</SelectItem>
            <SelectItem value="Email">Email</SelectItem>
            <SelectItem value="Webhook">Webhook</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <p className="text-sm text-muted-foreground">No items in the retry queue</p>
          <p className="text-xs text-muted-foreground mt-1">All outbound operations are succeeding 🎉</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted border-b border-border">
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground">Operation</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden sm:table-cell">Target</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground">Status</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground">Attempts</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Last Error</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden lg:table-cell">Next Retry</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {items.map(item => (
                <tr key={item.id} className={item.status === 'Failed' ? 'bg-destructive/5' : ''}>
                  <td className="px-3 py-3">
                    <span className="font-medium text-foreground text-xs">{item.operationType}</span>
                    <br />
                    <span className="text-xs text-muted-foreground">{item.operation}</span>
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground hidden sm:table-cell max-w-[160px] truncate">
                    {item.target}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[item.status] || 'bg-muted text-foreground'}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">
                    {item.attempts} / {item.maxAttempts}
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground hidden md:table-cell max-w-[200px] truncate" title={item.lastError}>
                    {item.lastError || '—'}
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground hidden lg:table-cell">
                    {item.status === 'Pending' || item.status === 'Retrying' ? timeUntil(item.nextRetryAt) : '—'}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1">
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 text-xs px-2"
                        disabled={acting === item.id}
                        onClick={() => handleAction(item.id, 'retry')}
                      >
                        {acting === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                      </Button>
                      {item.status === 'Failed' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-muted-foreground">
                              <X className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Dismiss this failed operation?</AlertDialogTitle>
                            </AlertDialogHeader>
                            <p className="text-sm text-muted-foreground">
                              This won't delete the record — it just removes it from the active view. The original {item.operationType} call to {item.target} will not be retried.
                            </p>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleAction(item.id, 'dismiss')}>Dismiss</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Safety lock banner */}
      <div className="rounded-lg border-2 border-amber-500/50 bg-amber-500/10 p-4 mt-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-amber-400 text-lg">🔒</span>
          <span className="font-semibold text-sm text-amber-300">Scheduled Processor: PAUSED / DISABLED</span>
        </div>
        <p className="text-xs text-amber-300/80">
          The retry queue scheduled task runs every 5 minutes but <strong>no processing occurs while the safety lock is on</strong> (ZITE_EMAIL_DELIVERY_DISABLED=true).
          The very first line of the processor checks the kill switch and returns immediately with zero work done — no emails, no SMS, no BMS calls, no external writes of any kind.
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          To resume processing, an Owner must set ZITE_EMAIL_DELIVERY_DISABLED to a value other than &quot;true&quot; in the app&apos;s environment variables.
        </p>
      </div>

      <p className="text-xs text-muted-foreground mt-3">
        Retry schedule: 5 min → 15 min → 1 hr → 4 hrs → permanent failure. Processor runs every 5 minutes — currently a <strong>no-op</strong> while safety lock is active.
      </p>
    </div>
  );
}
